// E-Sign Via Derwent — signer public page (drag-and-drop-aware, guided, mobile-first).
//
// Guided UX principles:
//  • Auto-fill date fields with today's date (locked — no click needed)
//  • Auto-scroll signer to the FIRST incomplete required field on load
//  • After completing a field, auto-jump to the NEXT required field
//  • Once every field is done, prompt them to scroll to the T&C at the bottom
//    with a bouncing down-arrow overlay
//  • T&C checkbox must be ticked to enable Submit (already the case)
//  • Mobile-first: sticky top progress + sticky bottom Next / Submit bar,
//    tap-friendly targets (>=44px), full-screen signature modal on phones
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ShieldCheck, Loader2, PenLine, CheckCircle2, AlertCircle, User, Mail,
  Calendar as CalIcon, TextCursor, CheckSquare, ArrowRight, ArrowDown, X,
} from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const API = process.env.REACT_APP_BACKEND_URL;
// Match the Fact Find / Client Portal: navy backdrop, Manrope, dark Derwent Finance
// logo on white cards, emerald accents.
const BRAND = '#2E333B';
const NAVY = '#2E333B';
const SHELL = { background: NAVY, fontFamily: "'Manrope', system-ui, sans-serif", WebkitTapHighlightColor: 'transparent' };
const TEXTURE = {
  backgroundImage: 'linear-gradient(135deg, transparent 0 48%, rgba(255,255,255,.06) 48% 52%, transparent 52%), radial-gradient(circle at 20% 10%, rgba(255,255,255,.12), transparent 40%), radial-gradient(circle at 85% 90%, rgba(255,255,255,.05), transparent 45%)',
  backgroundSize: '240px 240px, 100% 100%, 100% 100%',
};

const LogoHeader = () => (
  <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8">
    <img src="/derwent-finance-logo-dark.png" alt="Derwent Finance" className="h-8 sm:h-10 w-auto object-contain shrink-0" data-testid="esign-logo" draggable={false} />
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 sm:px-3 py-1 text-[10px] sm:text-[11px] font-bold text-emerald-700 shrink-0"><ShieldCheck className="h-3 w-3" /> Secure signing</span>
  </div>
);
const CreditFooter = () => (
  <p className="mt-5 mb-2 text-center text-[11px] sm:text-sm text-white/60 px-4 leading-relaxed">Authorised Credit Representative 500020 · Australian Credit License 384324</p>
);

const FIELD_ICON = {
  signature: PenLine, initials: PenLine, date: CalIcon,
  name: User, text: TextCursor, checkbox: CheckSquare,
};

const TODAY_STR = () => {
  const d = new Date();
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// A field COUNTS toward the "remaining" list only if the signer must interact with it.
// Dates are auto-filled → never count.
const needsSignerAction = (f, values) => {
  if (f.type === 'date') return false;
  const v = values[f.id];
  if (f.type === 'checkbox') return v !== 'true';
  return !v;
};

export default function EsignSigner() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pages, setPages] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [openFieldId, setOpenFieldId] = useState(null);
  const [tcVisible, setTcVisible] = useState(false);
  const tcRef = useRef(null);
  const containerRef = useRef(null);
  const [activeDocId, setActiveDocId] = useState(null);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const documents = doc?.documents || [];
  const multi = documents.length > 1;
  // Pick the first document this signer still has to sign
  useEffect(() => {
    if (!doc) return;
    setActiveDocId((cur) => {
      if (cur && documents.some((d) => d.id === cur && !d.signed_by_me)) return cur;
      const next = documents.find((d) => !d.signed_by_me && d.my_field_count > 0) || documents.find((d) => !d.signed_by_me) || documents[0];
      return next?.id || null;
    });
  }, [doc]);
  const activeDoc = documents.find((d) => d.id === activeDocId);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/esign/public/${token}`);
      setDoc(data);
      if (data.me?.name) setTypedName(data.me.name);
    } catch (e) {
      setError(e?.response?.data?.detail || 'This link is invalid or has expired');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Load PDF, adapt scale to viewport width for mobile-first rendering
  useEffect(() => {
    // Don't fetch the protected PDF until the signer has passed OTP verification —
    // otherwise the file endpoint 403s and shows a spurious "Failed to load" toast.
    if (!doc || doc.me?.status === 'signed' || !activeDocId || doc.verification_required) return;
    let cancelled = false;
    setPages([]); setPdfDoc(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/esign/public/${token}/file`, { responseType: 'blob', params: { document_id: activeDocId } });
        if (cancelled) return;
        const buf = await r.data.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPdfDoc(pdf);
        const pgs = [];
        const targetW = Math.min(window.innerWidth - 24, 900);
        for (let i = 1; i <= pdf.numPages; i += 1) {
          const p = await pdf.getPage(i);
          const baseVp = p.getViewport({ scale: 1 });
          const scale = targetW / baseVp.width;
          const viewport = p.getViewport({ scale });
          pgs.push({ page: p, width: viewport.width, height: viewport.height, scale });
        }
        setPages(pgs);
      } catch (e) {
        toast.error('Failed to load document');
      }
    })();
    return () => { cancelled = true; };
  }, [doc, token, activeDocId]);

  // Render canvases
  useEffect(() => {
    pages.forEach((p, idx) => {
      const el = document.getElementById(`sign-canvas-${activeDocId}-${idx + 1}`);
      if (!el || el.dataset.rendered === 'yes') return;
      const viewport = p.page.getViewport({ scale: p.scale });
      el.width = viewport.width;
      el.height = viewport.height;
      const ctx = el.getContext('2d');
      p.page.render({ canvasContext: ctx, viewport }).promise.then(() => {
        el.dataset.rendered = 'yes';
      });
    });
  }, [pages, activeDocId]);

  const myFields = useMemo(() => (doc?.fields || []).filter((f) => !activeDocId || (f.document_id || documents[0]?.id) === activeDocId), [doc, activeDocId, documents]);

  // Auto-fill date + checkbox defaults + confirmed name once
  useEffect(() => {
    if (!doc) return;
    setFieldValues((prev) => {
      const next = { ...prev };
      myFields.forEach((f) => {
        if (next[f.id] !== undefined) return;
        if (f.type === 'date') next[f.id] = TODAY_STR();
        if (f.type === 'checkbox') next[f.id] = 'false';
      });
      return next;
    });
  }, [doc]);

  const setValue = (id, v) => setFieldValues((prev) => ({ ...prev, [id]: v }));

  const remaining = useMemo(
    () => myFields.filter((f) => needsSignerAction(f, fieldValues)),
    [myFields, fieldValues],
  );
  const actionableTotal = useMemo(
    () => myFields.filter((f) => f.type !== 'date').length,
    [myFields],
  );
  const completed = actionableTotal - remaining.length;
  const allRequiredComplete = remaining.length === 0;

  // Track T&C visibility with IntersectionObserver
  useEffect(() => {
    if (!tcRef.current) return () => {};
    const obs = new IntersectionObserver(
      ([entry]) => setTcVisible(entry.isIntersecting),
      { threshold: 0.2 },
    );
    obs.observe(tcRef.current);
    return () => obs.disconnect();
  }, [pages.length, doc]);

  // Auto-scroll to next remaining field
  const jumpToNext = useCallback(() => {
    if (remaining.length === 0) {
      tcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const el = document.getElementById(`field-${remaining[0].id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setOpenFieldId(remaining[0].id);
  }, [remaining]);

  // On first load — auto-jump to the first field (once PDF pages have rendered)
  const jumpedInitially = useRef(false);
  useEffect(() => {
    if (jumpedInitially.current) return;
    if (pages.length === 0) return;
    if (remaining.length === 0) return;
    // Give the browser one paint to place the field overlays
    const t = setTimeout(() => {
      const el = document.getElementById(`field-${remaining[0].id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      jumpedInitially.current = true;
    }, 500);
    return () => clearTimeout(t);
  }, [pages.length, remaining]);

  // After committing a field, auto-jump to next
  const commitField = (id, value) => {
    setValue(id, value);
    setOpenFieldId(null);
    // Wait for state to flush + DOM to update
    setTimeout(() => {
      setFieldValues((prev) => {
        const rem = myFields.filter((f) => needsSignerAction(f, { ...prev }));
        if (rem.length > 0) {
          const el = document.getElementById(`field-${rem[0].id}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          tcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return prev;
      });
    }, 250);
  };

  const submit = async () => {
    if (!typedName.trim()) { toast.error('Please type your full name'); return; }
    if (!agreed) { toast.error('Please tick the Terms and Conditions checkbox to submit'); return; }
    if (!allRequiredComplete) { toast.error('Please complete every required field'); return; }
    setSigning(true);
    try {
      const r = await axios.post(`${API}/api/esign/public/${token}/complete`, {
        field_values: fieldValues,
        typed_name: typedName.trim(),
        agreed: true,
        ...(multi ? { document_id: activeDocId } : {}),
      });
      if (r.data.remaining_documents?.length) { toast.success(`${activeDoc?.name || 'Document'} signed — ${r.data.remaining_documents.length} more to go`); setAgreed(false); }
      else toast.success('Signed! Thank you.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Signing failed');
    } finally { setSigning(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/80 relative" style={SHELL}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.16]" style={TEXTURE} />
        <div className="relative flex items-center"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Opening your document…</div>
      </div>
    );
  }
  if (error || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative" style={SHELL}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.16]" style={TEXTURE} />
        <div className="relative w-full max-w-md rounded-[24px] bg-white shadow-2xl shadow-black/30 px-6 py-8 sm:px-10 sm:py-10">
          <LogoHeader />
          <div className="text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
            <p className="text-lg font-extrabold text-slate-900">Link unavailable</p>
            <p className="text-sm mt-2 text-slate-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const alreadySigned = doc.me?.status === 'signed';
  const declined = doc.me?.status === 'declined';
  const needsVerification = !!doc.verification_required && !alreadySigned && !declined;

  const requestOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/esign/public/${token}/request-otp`);
      setOtpRequested(true);
      if (data.dev_code) setDevCode(data.dev_code);
      toast.success(data.delivered ? 'Verification code sent to your mobile' : 'Verification code generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send code');
    } finally { setOtpBusy(false); }
  };
  const verifyOtp = async () => {
    if (otpCode.trim().length < 4) { toast.error('Enter the code we sent you'); return; }
    setOtpBusy(true);
    try {
      await axios.post(`${API}/api/esign/public/${token}/verify-otp`, { code: otpCode.trim() });
      toast.success('Identity verified');
      setOtpCode(''); setDevCode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally { setOtpBusy(false); }
  };

  const decline = async () => {
    const reason = window.prompt('Please tell us briefly why you are declining to sign — your broker will see this.');
    if (!reason || reason.trim().length < 2) return;
    try { await axios.post(`${API}/api/esign/public/${token}/decline`, { reason: reason.trim() }); toast.success('Your broker has been notified'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not decline'); }
  };
  const showScrollHint = allRequiredComplete && !agreed && !tcVisible;
  const canSubmit = allRequiredComplete && agreed && typedName.trim();
  const signPct = actionableTotal === 0 ? 100 : Math.round((completed / actionableTotal) * 100);

  if (needsVerification) {
    return (
      <div className="min-h-screen relative" style={SHELL}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.16]" style={TEXTURE} />
        <main className="relative mx-auto max-w-md px-3 sm:px-6 py-6 sm:py-12">
          <div className="rounded-[24px] sm:rounded-[28px] bg-white shadow-2xl shadow-black/30 px-5 py-7 sm:px-10 sm:py-10" data-testid="esign-verify-gate">
            <LogoHeader />
            <div className="text-center">
            <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${BRAND}14` }}>
              <ShieldCheck className="w-7 h-7" style={{ color: BRAND }} />
            </div>
            <p className="text-xl font-extrabold text-slate-950">Verify it&apos;s you</p>
            <p className="text-sm mt-2 text-slate-600">
              For your security, <span className="font-semibold">{doc.title}</span> from {doc.sender_name} is protected.
              We&apos;ll text a one-time code to your mobile ending {doc.phone_hint || '•••'}.
            </p>

            {!otpRequested ? (
              <button type="button" onClick={requestOtp} disabled={otpBusy} data-testid="esign-verify-send"
                      className="mt-6 w-full rounded-full text-sm font-bold py-3.5 text-white shadow-sm hover:opacity-90 disabled:opacity-50 min-h-[48px]"
                      style={{ backgroundColor: BRAND }}>
                {otpBusy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send my verification code'}
              </button>
            ) : (
              <div className="mt-6 space-y-3 text-left">
                {devCode && (
                  <p className="text-[11px] text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-1.5" data-testid="esign-verify-devcode">
                    SMS not configured — test code: <span className="font-mono font-bold">{devCode}</span>
                  </p>
                )}
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Enter the 6-digit code</span>
                  <input type="text" inputMode="numeric" maxLength={6} value={otpCode}
                         onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                         data-testid="esign-verify-code"
                         className="mt-1.5 w-full px-3 py-3 rounded-xl text-center text-2xl font-bold tracking-[0.4em] outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-200 min-h-[52px]"
                         placeholder="••••••" />
                </label>
                <button type="button" onClick={verifyOtp} disabled={otpBusy} data-testid="esign-verify-submit"
                        className="w-full rounded-full text-sm font-bold py-3.5 text-white shadow-sm hover:opacity-90 disabled:opacity-50 min-h-[48px]"
                        style={{ backgroundColor: BRAND }}>
                  {otpBusy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Verify & open document'}
                </button>
                <button type="button" onClick={requestOtp} disabled={otpBusy} data-testid="esign-verify-resend"
                        className="w-full text-xs font-semibold text-slate-500 hover:text-slate-800 min-h-[36px]">
                  Didn&apos;t get it? Resend code
                </button>
              </div>
            )}
            <p className="mt-5 text-[11px] text-slate-400">This code expires in 5 minutes and is required each time you open the document on a new device.</p>
            </div>
          </div>
          <CreditFooter />
        </main>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative" style={SHELL}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.16]" style={TEXTURE} />
        <div className="relative w-full max-w-md rounded-[24px] bg-white shadow-2xl shadow-black/30 px-6 py-8 sm:px-10 sm:py-10" data-testid="esign-declined-state">
          <LogoHeader />
          <div className="text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
            <p className="text-lg font-extrabold text-slate-900">You declined to sign</p>
            <p className="text-sm mt-2 text-slate-500">{doc.sender_name} has been notified and will be in touch. If this was a mistake, please contact your broker for a fresh link.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pb-36 sm:pb-28" style={SHELL} data-testid="esign-signer-shell">
      <div className="pointer-events-none absolute inset-0 opacity-[0.16]" style={TEXTURE} />
      {/* Sticky signing-progress header — matches Fact Find */}
      {!alreadySigned && myFields.length > 0 && (
        <div className="sticky top-0 z-30 border-b border-white/10 bg-[#2E333B]/90 backdrop-blur-md pt-[env(safe-area-inset-top)]" data-testid="esign-progress-header">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-2 sm:py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-white/60">Secure Signing</p>
                <p className="text-xs sm:text-sm font-semibold text-white truncate">{allRequiredComplete ? (agreed ? 'Ready to submit' : 'All fields done — accept Terms') : 'In progress'}{doc.me?.name ? ` · ${doc.me.name.split(' ')[0]}` : ''}</p>
              </div>
              <p className="text-[22px] sm:text-3xl font-extrabold text-white tabular-nums leading-none">{signPct}%</p>
            </div>
            <div className="mt-1.5 sm:mt-2 h-2 rounded-full bg-white/15 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ease-out ${allRequiredComplete ? 'bg-[#86EFAC]' : 'bg-gradient-to-r from-[#A7F3D0] to-[#86EFAC]'}`} style={{ width: `${signPct}%` }} data-testid="esign-progress-bar" /></div>
          </div>
        </div>
      )}

      {alreadySigned ? (
        <main className="relative mx-auto max-w-3xl px-3 sm:px-6 py-6 sm:py-12">
          <div className="rounded-[24px] sm:rounded-[28px] bg-white shadow-2xl shadow-black/30 px-5 py-7 sm:px-12 sm:py-12">
            <LogoHeader />
            <div className="rounded-2xl p-6 sm:p-8 text-center bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-600" />
              <p className="text-xl sm:text-2xl font-extrabold text-emerald-800">Thank you — you&apos;ve signed</p>
              <p className="text-sm mt-2 text-emerald-700">
                Your electronic signature was captured on {new Date(doc.me.signed_at).toLocaleString('en-AU')}. {doc.other_signers_count ? 'A completed copy will be sent to you once all parties have signed.' : 'Your signed copy has been saved and your broker has been notified.'}
              </p>
              {doc.me?.return_url && <a href={doc.me.return_url} data-testid="esign-return-to-portal" className="mt-5 inline-flex items-center justify-center rounded-xl bg-[#2E333B] px-6 py-3 text-sm font-bold text-white hover:bg-[#3D434D]">Continue to your Client Portal</a>}
            </div>
          </div>
        </main>
      ) : (
        <main className="relative mx-auto max-w-3xl px-2.5 sm:px-6 py-3 sm:py-6 space-y-3 sm:space-y-5">
          {/* Doc summary card */}
          <div className="rounded-[24px] sm:rounded-[28px] p-4 py-6 sm:p-8 bg-white shadow-2xl shadow-black/30">
            <LogoHeader />
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-lg bg-[#F4F4F6] flex items-center justify-center">
                <PenLine className="w-3 h-3 text-[#2E333B]" strokeWidth={2.4} />
              </span>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                E-Sign Via Derwent · Ref {doc.reference_number}
              </p>
            </div>
            <p className="text-xl sm:text-2xl font-extrabold text-slate-950 mt-1" data-testid="esign-doc-title">{doc.title}</p>
            {doc.message && <p className="text-sm mt-2 leading-relaxed text-slate-700">{doc.message}</p>}
            {multi && (
              <div className="mt-4" data-testid="esign-doc-package">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">This request has {documents.length} documents · {documents.filter((d) => d.signed_by_me).length} of {documents.length} signed</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {documents.map((d, i) => (
                    <button key={d.id} type="button" onClick={() => !d.signed_by_me && setActiveDocId(d.id)} data-testid={`esign-doc-tab-${d.id}`}
                            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border ${d.signed_by_me ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : activeDocId === d.id ? 'text-white border-transparent' : 'bg-white text-slate-700 border-slate-200'}`}
                            style={!d.signed_by_me && activeDocId === d.id ? { backgroundColor: BRAND } : undefined}>
                      {d.signed_by_me ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-4 h-4 rounded-full bg-white/20 text-[10px] inline-flex items-center justify-center">{i + 1}</span>}
                      {d.name}{d.signed_by_me ? ' · signed' : d.my_field_count === 0 ? ' · read only' : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-slate-500">
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> From {doc.sender_name}</span>
              {doc.sender_email && <span className="hidden sm:flex items-center gap-1"><Mail className="w-3 h-3" /> {doc.sender_email}</span>}
            </div>

            {myFields.length > 0 && (
              <button type="button" onClick={jumpToNext} data-testid="esign-start"
                      className="mt-4 w-full sm:w-auto rounded-full text-sm font-bold px-6 py-3 text-white shadow-sm hover:opacity-90 inline-flex items-center justify-center gap-2 min-h-[44px]"
                      style={{ backgroundColor: BRAND }}>
                {completed === 0 ? 'Start signing' : (remaining.length > 0 ? 'Continue signing' : 'Review Terms & Conditions')}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <button type="button" onClick={decline} data-testid="esign-decline" className="mt-3 sm:ml-4 block sm:inline text-xs font-semibold text-slate-400 hover:text-rose-600 underline underline-offset-2">I need to decline this document</button>
          </div>

          {/* PDF pages */}
          <div ref={containerRef} className="space-y-6">
            {!pdfDoc && (
              <div className="text-center py-16 text-white/70">
                <Loader2 className="w-6 h-6 animate-spin inline-block" /> Loading document…
              </div>
            )}
            {pages.map((p, idx) => {
              const pageFields = myFields.filter((f) => f.page === idx + 1);
              return (
                <div key={`${activeDocId}-${idx}`} className="mx-auto" style={{ maxWidth: p.width }}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-white/70 mb-1.5 px-1">
                    Page {idx + 1} of {pages.length}
                  </p>
                  <div className="relative shadow-lg rounded-lg overflow-hidden bg-white" data-testid={`sign-page-${idx + 1}`}>
                    <canvas id={`sign-canvas-${activeDocId}-${idx + 1}`} className="block w-full h-auto" />
                    {pageFields.map((f) => (
                      <FieldOverlay
                        key={f.id} field={f}
                        value={fieldValues[f.id]}
                        onChange={(v) => setValue(f.id, v)}
                        onCommit={(v) => commitField(f.id, v)}
                        isOpen={openFieldId === f.id}
                        onOpen={() => setOpenFieldId(f.id)}
                        onClose={() => setOpenFieldId(null)}
                        typedName={typedName}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Terms and Conditions section — the FINAL gate */}
          <div ref={tcRef} className="rounded-[24px] sm:rounded-[28px] p-4 py-6 sm:p-8 bg-white shadow-2xl shadow-black/30"
               data-testid="esign-tc-section">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-slate-400" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Terms &amp; Conditions</p>
            </div>

            <label className="block mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Full legal name *</span>
              <input type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)}
                     placeholder="Your full name as it appears on ID"
                     data-testid="esign-typed-name"
                     className="mt-1.5 w-full px-3 py-3 rounded-xl text-base outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-200 min-h-[48px]" />
            </label>

            <label className="mt-4 flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100"
                   data-testid="esign-tc-checkbox-wrapper">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                     data-testid="esign-agree"
                     className="mt-0.5 w-5 h-5" style={{ accentColor: BRAND }} />
              <span className="text-xs sm:text-sm leading-relaxed text-slate-700">
                <strong>I agree to the Terms and Conditions.</strong> My electronic signature is the legal equivalent of my handwritten signature. I confirm I have reviewed the document above and consent to its contents. My IP address, timestamp and browser are recorded for audit purposes.
              </span>
            </label>

            <button type="button" onClick={submit} disabled={signing || !canSubmit}
                    data-testid="esign-submit"
                    className="mt-5 w-full rounded-full text-base font-bold py-4 text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-50 min-h-[52px]"
                    style={{ backgroundColor: BRAND }}>
              {signing ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (<><ShieldCheck className="w-5 h-5 inline-block mr-1.5 -mt-0.5" /> Sign document</>)}
            </button>

            {!canSubmit && (
              <p className="mt-3 text-[11px] text-center text-slate-500">
                {!allRequiredComplete && `${remaining.length} field${remaining.length === 1 ? '' : 's'} still to complete · `}
                {!typedName.trim() && 'Full legal name required · '}
                {!agreed && 'Terms and Conditions must be accepted'}
              </p>
            )}
          </div>
        </main>
      )}

      {/* Sticky bottom action bar (mobile-first — shown on all screens for consistency) */}
      {!alreadySigned && myFields.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-lg">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-300"
                     style={{
                       width: `${actionableTotal === 0 ? 100 : (completed / actionableTotal) * 100}%`,
                       backgroundColor: allRequiredComplete ? '#059669' : BRAND,
                     }} />
              </div>
              <p className="text-[10px] sm:text-[11px] text-slate-500 mt-1 leading-tight">
                {allRequiredComplete
                  ? (agreed ? 'Ready to submit' : 'All fields done · scroll to Terms and Conditions')
                  : `${completed} of ${actionableTotal} field${actionableTotal === 1 ? '' : 's'} complete`}
              </p>
            </div>
            {!allRequiredComplete ? (
              <button type="button" onClick={jumpToNext} data-testid="esign-next-field"
                      className="inline-flex items-center gap-1 rounded-full text-sm font-bold px-4 sm:px-5 py-2.5 sm:py-3 text-white shadow-sm hover:opacity-90 min-h-[44px]"
                      style={{ backgroundColor: BRAND }}>
                Next field <ArrowRight className="w-4 h-4" />
              </button>
            ) : !agreed ? (
              <button type="button" onClick={() => tcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      data-testid="esign-scroll-to-tc"
                      className="inline-flex items-center gap-1 rounded-full text-sm font-bold px-4 sm:px-5 py-2.5 sm:py-3 text-white shadow-sm hover:opacity-90 min-h-[44px] animate-pulse"
                      style={{ backgroundColor: BRAND }}>
                Accept T&amp;Cs <ArrowDown className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={signing || !canSubmit}
                      data-testid="esign-submit-sticky"
                      className="inline-flex items-center gap-1 rounded-full text-sm font-bold px-5 py-2.5 sm:py-3 text-white shadow-sm hover:opacity-90 disabled:opacity-60 min-h-[44px]"
                      style={{ backgroundColor: '#059669' }}>
                {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit <ShieldCheck className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Floating "Scroll to bottom" hint — only when fields done but T&C not visible & not ticked */}
      {!alreadySigned && showScrollHint && (
        <button type="button"
                onClick={() => tcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                data-testid="esign-almost-done-arrow"
                className="fixed left-1/2 -translate-x-1/2 bottom-24 sm:bottom-24 z-40 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl shadow-2xl text-white font-bold text-xs sm:text-sm max-w-[92vw] text-center"
                style={{ backgroundColor: BRAND }}>
          <span>Almost done — tap here to accept T&amp;Cs and finish</span>
          <ArrowDown className="w-6 h-6 animate-bounce" />
        </button>
      )}

      <footer className="relative mx-auto max-w-3xl px-4 sm:px-6 pb-8">
        <CreditFooter />
        <p className="text-center text-[10px] sm:text-[11px] text-white/40 mt-1">© {new Date().getFullYear()} {doc?.branding?.business_name || 'Derwent Finance'} · E-Sign Via Derwent</p>
      </footer>
    </div>
  );
}

// ─── Field overlay ─────────────────────────────────────────────────
const FieldOverlay = ({ field, value, onChange, onCommit, isOpen, onOpen, onClose, typedName }) => {
  const Icon = FIELD_ICON[field.type] || TextCursor;
  const filled = field.type === 'checkbox' ? value === 'true' : !!value;
  const isDate = field.type === 'date';

  // Dates are auto-filled and locked — no click handler
  const openIfEditable = () => {
    if (isDate) return; // locked
    if (field.type === 'checkbox') {
      const newVal = value === 'true' ? 'false' : 'true';
      onCommit(newVal);
      return;
    }
    onOpen();
  };

  const style = {
    left: `${field.x * 100}%`,
    top: `${field.y * 100}%`,
    width: `${field.w * 100}%`,
    height: `${field.h * 100}%`,
    backgroundColor: filled ? '#FFFFFFF2' : `${BRAND}22`,
    border: `1.5px ${filled ? 'solid' : 'dashed'} ${isDate ? '#94A3B8' : BRAND}`,
    // Ensure a comfortable tap-target — expand click area invisibly on tiny fields
    minHeight: '20px',
    minWidth: '20px',
  };

  const renderContent = () => {
    if (!filled) {
      return (
        <div className="w-full h-full flex items-center justify-center gap-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-1"
             style={{ color: BRAND }}>
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{field.label || field.type}</span>
        </div>
      );
    }
    if (field.type === 'signature' || field.type === 'initials') {
      return <img src={value} alt="signature" className="w-full h-full object-contain" />;
    }
    if (field.type === 'checkbox') {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <CheckCircle2 className="w-4 h-4" style={{ color: BRAND }} />
        </div>
      );
    }
    return (
      <div className="w-full h-full flex items-center px-1 text-[10px] sm:text-[11px] text-slate-900 truncate">
        {value}
      </div>
    );
  };

  return (
    <>
      <div id={`field-${field.id}`}
           data-testid={`sign-field-${field.id}`}
           className={`absolute rounded overflow-hidden transition-all ${isDate ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02]'}`}
           style={style}
           onClick={openIfEditable}>
        {renderContent()}
      </div>
      {isOpen && !isDate && field.type !== 'checkbox' && (
        <FieldModal field={field} value={value} onCommit={onCommit} onClose={onClose} typedName={typedName} />
      )}
    </>
  );
};

// ─── Field modal (mobile-first, full-screen on phones) ─────────────
const FieldModal = ({ field, value, onCommit, onClose, typedName }) => {
  const [localVal, setLocalVal] = useState(value || '');
  const canvasRef = useRef(null);
  const isSigLike = field.type === 'signature' || field.type === 'initials';

  useEffect(() => {
    if (!isSigLike) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0F172A';
    let drawing = false, last = null;
    const getPos = (e) => {
      const rect = c.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: (p.clientX - rect.left) * (c.width / rect.width),
               y: (p.clientY - rect.top) * (c.height / rect.height) };
    };
    const start = (e) => { drawing = true; last = getPos(e); e.preventDefault(); };
    const move = (e) => {
      if (!drawing) return;
      const p = getPos(e);
      ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      last = p; e.preventDefault();
    };
    const stop = () => { drawing = false; };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move);
    c.addEventListener('mouseup', stop);   c.addEventListener('mouseleave', stop);
    c.addEventListener('touchstart', start, { passive: false });
    c.addEventListener('touchmove', move, { passive: false });
    c.addEventListener('touchend', stop);
    return () => {
      c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move);
      c.removeEventListener('mouseup', stop);    c.removeEventListener('mouseleave', stop);
      c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', stop);
    };
  }, [isSigLike]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false;
    return true;
  };

  const commit = () => {
    if (isSigLike) {
      if (isCanvasEmpty()) { toast.error('Please draw your signature'); return; }
      onCommit(canvasRef.current.toDataURL('image/png'));
    } else {
      if (!localVal.trim()) { toast.error('Please enter a value'); return; }
      onCommit(localVal.trim());
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="field-modal">
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-slate-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Complete field</p>
            <p className="text-base font-extrabold text-slate-900 capitalize">{field.label || field.type}</p>
          </div>
          <button type="button" onClick={onClose} className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-slate-100" data-testid="field-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {isSigLike && (
            <>
              <div className="rounded-xl border-2 border-dashed border-slate-300 overflow-hidden bg-white" style={{ aspectRatio: '3 / 1' }}>
                <canvas ref={canvasRef} width={900} height={300} data-testid="field-canvas"
                        className="w-full h-full cursor-crosshair" style={{ touchAction: 'none' }} />
              </div>
              <div className="flex items-center justify-between">
                <button type="button" onClick={clearCanvas} className="text-xs font-bold text-slate-500 hover:text-slate-700 min-h-[36px] px-2">Clear</button>
                <p className="text-[11px] text-slate-400">Use your finger, stylus or mouse</p>
              </div>
            </>
          )}
          {field.type === 'text' && (
            <input type="text" value={localVal} onChange={(e) => setLocalVal(e.target.value)}
                   data-testid="field-text-input" autoFocus
                   className="w-full px-3 py-3 rounded-xl text-base outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-200 min-h-[48px]"
                   placeholder="Type here" />
          )}
          {field.type === 'name' && (
            <>
              <input type="text" value={localVal} onChange={(e) => setLocalVal(e.target.value)}
                     data-testid="field-name-input" autoFocus
                     className="w-full px-3 py-3 rounded-xl text-base outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-200 min-h-[48px]"
                     placeholder="Your full name" />
              {typedName && localVal !== typedName && (
                <button type="button" onClick={() => setLocalVal(typedName)} className="text-xs font-bold min-h-[36px]" style={{ color: BRAND }}>
                  Use my confirmed name ({typedName})
                </button>
              )}
            </>
          )}
          <button type="button" onClick={commit} data-testid="field-commit"
                  className="w-full rounded-full text-base font-bold py-3.5 text-white shadow-sm hover:opacity-90 min-h-[52px]"
                  style={{ backgroundColor: BRAND }}>
            Save &amp; continue
          </button>
        </div>
      </div>
    </div>
  );
};
